import React from 'react';
import type { ColumnQualityRow } from '../../types';
import { ScoreCell, MetricCell } from '../status/Indicators';

/**
 * Stage 6 — Column Quality Analysis table (6 columns, sorted DESC by Score).
 *
 *   Column Name | Score | Fill Rate | Uniqueness | Invalid Count | Recommendation
 *
 * Visual conventions matching Reltio:
 *   - No vertical column borders, only horizontal row dividers
 *   - Sticky header (stays visible when scrolling long tables)
 *   - Per-row max-severity glow on the Score cell (subtle red ring on critical rows)
 *   - Recommendation cell wraps multi-line; CRITICAL prefix highlighted red
 */

interface ColumnQualityTableProps {
  rows: ColumnQualityRow[];
  className?: string;
}

export const ColumnQualityTable: React.FC<ColumnQualityTableProps> = ({
  rows, className = '',
}) => (
  <div className={`overflow-hidden rounded-lg border border-cargo-border ${className}`}>
    <table className="w-full text-[13px] leading-5">
      <thead className="bg-cargo-panel text-cargo-muted text-[11px] uppercase tracking-wider font-medium sticky top-0 z-10">
        <tr>
          <th className="text-left px-4 py-3 align-bottom w-[18%]">Column<br />Name</th>
          <th className="text-left px-4 py-3 align-bottom w-[10%]">Score</th>
          <th className="text-left px-4 py-3 align-bottom w-[11%]">Fill<br />Rate</th>
          <th className="text-left px-4 py-3 align-bottom w-[12%]">Uniqueness</th>
          <th className="text-left px-4 py-3 align-bottom w-[11%]">Invalid<br />Count</th>
          <th className="text-left px-4 py-3 align-bottom">Recommendation</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-cargo-border">
        {rows.map((row) => (
          <QualityRow key={row.column_name} row={row} />
        ))}
      </tbody>
    </table>
  </div>
);

const QualityRow: React.FC<{ row: ColumnQualityRow }> = ({ row }) => {
  const isCritical = row.is_critical;
  const recommendation = row.recommendation;
  const hasCritical = recommendation.startsWith('CRITICAL');
  const recBody = hasCritical
    ? recommendation.slice('CRITICAL'.length).replace(/^\s*-\s*/, '')
    : recommendation;

  return (
    <tr
      className={`
        hover:bg-cargo-panel transition-colors
        ${isCritical ? 'bg-cargo-error/10 border-l-2 border-cargo-error' : ''}
      `}
    >
      <td className="px-4 py-3.5 text-cargo-text font-medium align-top">
        {row.column_name}
      </td>
      <td className="px-4 py-3.5 align-top">
        <ScoreCell value={row.score.value} bucket={row.score.bucket} />
      </td>
      <td className="px-4 py-3.5 align-top">
        <MetricCell value={row.fill_rate.value} status={row.fill_rate.status} format="percent" />
      </td>
      <td className="px-4 py-3.5 align-top">
        <MetricCell value={row.uniqueness.value} status={row.uniqueness.status} format="percent" />
      </td>
      <td className="px-4 py-3.5 align-top">
        <MetricCell value={row.invalid_count.value} status={row.invalid_count.status} format="integer" />
      </td>
      <td className="px-4 py-3.5 text-cargo-muted align-top leading-relaxed">
        {hasCritical && (
          <span className="text-cargo-error font-semibold">CRITICAL</span>
        )}
        {hasCritical && <span className="text-cargo-muted"> - </span>}
        <span>{recBody}</span>
      </td>
    </tr>
  );
};
